import React, { useMemo, useRef, useState } from 'react'
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import numeral from 'numeral'
import { Tooltip } from 'react-tooltip'

import styles from './styles.module.scss'
import Table from '../Table'
import { EditIcon, PencilIcon } from '../Icons'
import InputSelect from '../../Core/InputSelect'
import { getCurrency } from '../../../helpers/currencies'

const EMPTY_ROW = (defaultVat) => {
  const price = 100
  const quantity = 1
  const totalWithoutVat = price * quantity
  const totalWithVat = totalWithoutVat * (1 + (defaultVat / 100))
  return {
    id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    description: '',
    quantity: quantity,
    unit_price: price,
    vat: numeral(totalWithVat - totalWithoutVat).format('0.00'),
    total_price: numeral(totalWithVat).format('0.00'),
    type: 'manual_entry',
  }
}

const TableInput = ({value, onChange, onBlur, focused, type = 'number', placeholder}) => {
  return (
    <div className={styles.inputContainer}>
      <input
        className={styles.input}
        type={type}
        value={value}
        onChange={e => {
          onChange(e.target.value)
        }}
        onBlur={onBlur}
        autoFocus={focused}
        placeholder={placeholder}
      />
    </div>
  )
}

const columnHelper = createColumnHelper()

const getSortNumber = value => {
  const parsed = Number(value)

  return Number.isNaN(parsed) ? 0 : parsed
}

const getTypeLabel = (sourceType, t) => {
  if (sourceType === 'work_time') {
    return t('Work')
  }

  if (sourceType === 'expense') {
    return t('Expense')
  }

  return t('Manual')
}

const ManualEntries = ({ entries = [], currency, defaultVat, groupBy, markup, discount, onChange, onChangeGroupBy, onChangeMarkup, onEditEntry, onAddEntry, onRemoveEntry }) => {

  const { t } = useTranslation()

  const editableKeyToFocus = useRef(null)
  const originalEditRowValues = useRef({})
  const [sorting, setSorting] = useState([])
  const [editingRowKey, setEditingRowKey] = useState(null)

  const groupingOptions = [
    { id: 'detailed', name: t('One line per entry') },
    { id: 'summarized', name: t('One summarized line') },
    { id: 'by_employee', name: t('By employee') },
    { id: 'by_project', name: t('By project') },
    { id: 'by_date', name: t('By date') },
    { id: 'by_work_type', name: t('By work type') },
  ]

  const markupTypeOptions = [
    { id: 'percentage', name: t('Percentage') },
    { id: 'fixed', name: t('Fixed amount') },
  ]

  const handleChange = (row, field, value) => {
    editableKeyToFocus.current = `${row.id}_${field}`
    const updated = {
      ...row,
      [field]: value,
    }

    if (field === 'quantity' || field === 'unit_price' || field === 'vat') {
      const qty = field === 'quantity' ? Number(value) : Number(updated.quantity)
      const price = field === 'unit_price' ? Number(value) : Number(updated.unit_price)
      const vat = defaultVat //field === 'vat' ? Number(value) : Number(updated.vat)
      const vatMultiplier = 1 + ((Number.isNaN(vat) ? 0 : vat) / 100)

      if (!Number.isNaN(qty) && !Number.isNaN(price)) {
        updated.vat = numeral((price * qty * vatMultiplier) - (price * qty)).format('0.00')
        updated.total_price = numeral(qty * price * vatMultiplier).format('0.00')
      }
    }

    if (field === 'total_price') {
      const qty = Number(updated.quantity)
      const total = Number(value)
      const vat = defaultVat
      const vatMultiplier = 1 + ((Number.isNaN(vat) ? 0 : vat) / 100)

      if (!Number.isNaN(qty) && qty !== 0 && !Number.isNaN(total) && vatMultiplier !== 0) {
        const priceWithVat = total / qty
        const priceWithoutVat = priceWithVat / vatMultiplier
        updated.unit_price = numeral(priceWithoutVat).format('0.00')
        updated.vat = numeral(priceWithVat*qty - priceWithoutVat*qty).format('0.00')
      }
    }

    onEditEntry(updated)
  }

  const handleAddRow = () => {
    onAddEntry(EMPTY_ROW(defaultVat))
  }

  const handleRemoveRow = (row) => {
    onRemoveEntry(row.id)
  }

  const getDisplayValue = value => {
    if (value === null || value === undefined || value === '') {
      return '-'
    }

    return value
  }

  const handleStartEdit = (row) => {
    setEditingRowKey(row.id)
    originalEditRowValues.current = row
  }

  const handleCancelEdit = () => {
    setEditingRowKey(null)
    onEditEntry(originalEditRowValues.current)
    originalEditRowValues.current = {}
  }

  const handleSaveEdit = (row) => {
    setEditingRowKey(null)
    originalEditRowValues.current = {}
    onEditEntry({...row, edited: true})
  }

  const columns = useMemo(() => [
    columnHelper.accessor('description', {
      enableSorting: false,
      header: t('Description'),
      cell: ({ row, column }) => {
        if (editingRowKey === row.original.id || row.original.type === 'manual_entry') {
          return (
            <TableInput
              type="text"
              value={row.original[column.id]}
              onChange={value => handleChange(row.original, column.id, value)}
              onBlur={() => editableKeyToFocus.current = null}
              focused={editableKeyToFocus.current === `${row.original.id}_${column.id}`}
              placeholder={t('Description')} />
          )
        }
        return (
          <div className={styles.readOnlyValue}>{row.original.description.replace(/\n/g, ' • ')}</div>
        )
      },
    }),
    columnHelper.accessor(row => getSortNumber(row.quantity), {
      id: 'quantity',
      header: t('Quantity'),
      cell: ({ row, column }) => {
        if (editingRowKey === row.original.id || row.original.type === 'manual_entry') {
          return (
            <TableInput
              value={row.original[column.id]}
              onChange={value => handleChange(row.original, column.id, value)}
              onBlur={() => editableKeyToFocus.current = null}
              focused={editableKeyToFocus.current === `${row.original.id}_${column.id}`}
              placeholder="0" />
          )
        }
        return (
          <div className={styles.readOnlyValue}>{getDisplayValue(row.original.quantity)}</div>
        )
      },
    }),
    columnHelper.accessor(row => getSortNumber(row.unit_price), {
      id: 'unit_price',
      header: `${t('Unit price')}, ${getCurrency(currency).symbol}`,
      cell: ({ row, column }) => {
        if (editingRowKey === row.original.id || row.original.type === 'manual_entry') {
          return (
            <TableInput
              value={row.original[column.id]}
              onChange={value => handleChange(row.original, column.id, value)}
              onBlur={() => editableKeyToFocus.current = null}
              focused={editableKeyToFocus.current === `${row.original.id}_${column.id}`}
              placeholder="0" />
          )
        }
        return (
          <div className={styles.readOnlyValue}>{getDisplayValue(row.original.unit_price)}</div>
        )
      },
    }),
    columnHelper.accessor(row => getSortNumber(row.vat), {
      id: 'vat',
      header: `${t('VAT')}, ${getCurrency(currency).symbol}`,
      cell: ({ row, column }) => {
        if (editingRowKey === row.original.id || row.original.type === 'manual_entry') {
          return (
            <TableInput
              value={row.original[column.id]}
              onChange={value => handleChange(row.original, column.id, value)}
              onBlur={() => editableKeyToFocus.current = null}
              focused={editableKeyToFocus.current === `${row.original.id}_${column.id}`}
              placeholder="0" />
          )
        }
        return (
          <div className={styles.readOnlyValue}>{getDisplayValue(row.original.vat)}</div>
        )
      },
    }),
    columnHelper.accessor(row => getSortNumber(row.total_price), {
      id: 'total_price',
      header: `${t('Total')}, ${getCurrency(currency).symbol}`,
      cell: ({ row, column }) => {
        if (editingRowKey === row.original.id || row.original.type === 'manual_entry') {
          return (
            <TableInput
              value={row.original[column.id]}
              onChange={value => handleChange(row.original, column.id, value)}
              onBlur={() => editableKeyToFocus.current = null}
              focused={editableKeyToFocus.current === `${row.original.id}_${column.id}`}
              placeholder="0" />
          )
        }
        return (
          <div className={styles.readOnlyValue}>{getDisplayValue(row.original.total_price)}</div>
        )
      },
    }),
    columnHelper.accessor('type', {
      id: 'type',
      header: t('Type'),
      cell: ({ row }) => {
        const isEditable = groupBy === 'detailed' && row.original.type !== 'manual_entry'
        return (
          isEditable
            ? editingRowKey === row.original.id
              ? <div className={styles.editActions}>
                  <button type="button" className={styles.cancelBtn} onClick={handleCancelEdit}>{t('Cancel')}</button>
                  <button type="button" className={styles.saveBtn} onClick={() => handleSaveEdit(row.original)}>{t('Save')}</button>
                </div>
              : <div className={styles.readOnlyTypeCell}>
                  <div className={styles.readOnlyMark}>
                    {getTypeLabel(row.original.type, t)}
                    {
                      row.original.edited
                        ? <div className={styles.pencilIconWrapper} data-tooltip-id={`edited`} data-tooltip-content={t('Edited')}>
                            <PencilIcon />
                          </div>
                        : null
                    }
                  </div>
                  <div className={styles.editIconWrapper} onClick={() => handleStartEdit(row.original)}>
                    <EditIcon className={styles.editIcon} />
                  </div>
                </div>
            : <div className={styles.readOnlyTypeCell}>
                <div className={styles.readOnlyMark}>
                  {getTypeLabel(row.original.type, t)}
                </div>
                {
                  row.original.type == 'manual_entry'
                    ? <button
                      type="button"
                      className={styles.removeBtn}
                      onClick={() => handleRemoveRow(row.original)}
                      title="Remove row"
                    >
                      &times;
                    </button>
                    : null
                }
              </div>
        )
      },
    }),
  ], [handleChange, handleRemoveRow, handleStartEdit, handleCancelEdit, handleSaveEdit, editingRowKey, t])

  const table = useReactTable({
    data: entries,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: row => String(row.id),
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  })

  return (
    <div className={styles.container}>
      <div className={styles.tools}>
        <div className={styles.groupByWrapper}>
          <InputSelect
            id='group_by'
            placeholder={t('Group by')}
            options={groupingOptions}
            value={groupBy}
            name='group_by'
            onChange={onChangeGroupBy}
            valueKey='id'
            labelKey='name'
          />
        </div>
        <div style={{display: 'flex', alignItems: 'center', flex: 1}}>
          <div className={styles.markupControlItem} style={{minWidth: 410}}>
            <div className={styles.markupLabel}>{t('Work markup')}</div>
            <div className={styles.markupInputsRow}>
              <InputSelect
                id='work_times_markup_type'
                options={markupTypeOptions}
                value={markup?.work_times_markup_type || 'percentage'}
                name='work_times_markup_type'
                onChange={onChangeMarkup}
                valueKey='id'
                labelKey='name'
              />
              <input
                className={styles.markupInput}
                type='number'
                min='0'
                name='work_times_markup_value'
                value={markup?.work_times_markup_value ?? 0}
                onChange={e => {
                  editableKeyToFocus.current = null
                  onChangeMarkup(e)
                }}
                placeholder={t('Value')}
              />
            </div>
          </div>
          <div className={styles.markupControlItem} style={{minWidth: 410}}>
            <div className={styles.markupLabel}>{t('Expenses markup')}</div>
            <div className={styles.markupInputsRow}>
              <InputSelect
                id='expenses_markup_type'
                options={markupTypeOptions}
                value={markup?.expenses_markup_type || 'percentage'}
                name='expenses_markup_type'
                onChange={onChangeMarkup}
                valueKey='id'
                labelKey='name'
              />
              <input
                className={styles.markupInput}
                type='number'
                min='0'
                name='expenses_markup_value'
                value={markup?.expenses_markup_value ?? 0}
                onChange={e => {
                  editableKeyToFocus.current = null
                  onChangeMarkup(e)
                }}
                placeholder={t('Value')}
              />
            </div>
          </div>
          <div className={styles.markupControlItem}>
            <div className={styles.markupLabel}>{t('Discount')}</div>
            <input
              className={styles.markupInput}
              type='number'
              min='0'
              name='discount'
              value={discount ?? 0}
              onChange={e => {
                editableKeyToFocus.current = null
                onChange({discount: e.target.value})
              }}
            />
            <div>{getCurrency(currency).symbol}</div>
          </div>
        </div>
        <button type="button" className={styles.addBtn} onClick={handleAddRow}>
          + {t('Add row')}
        </button>
      </div>
      <Table table={table} columns={columns} headerClassName={styles.tableHeader} />
      <Tooltip
        id="edited"
        className={styles.tooltip} />
    </div>
  )
}

export default ManualEntries
